import React from "react";
import { TONE_CLASSES } from "../subscriptionState";
import type { TrialCountdown } from "../trialCountdown";
import PlanIncludes from "./PlanIncludes";

interface TrialPlanCardProps {
  /** What the product is called — "Naurist Pro". The card adds "(Trial)". */
  productName: string;
  /** How much of the fortnight is left, off the trial the server reported —
   *  see `trialCountdown`. */
  countdown: TrialCountdown;
}

/**
 * The plan a workspace is on while it is on the free trial.
 *
 * `SubscriptionStatusCard`'s counterpart, and deliberately not the same
 * component: that one is built around a `Subscription`, and during a trial there
 * is none — the backend answers `subscription: null` with `access_source:
 * "FREE_TRIAL"`. Everything that card reads off the record (the cycle, the
 * amount, the next billing date, a mandate to mend) simply does not exist here,
 * and a card that took a nullable subscription would be two cards wearing one
 * name.
 *
 * What is the same is what matters to the reader: the product they have, that it
 * is running, and what it includes. So it leads with the plan and folds the
 * feature list away under the same disclosure — see `PlanIncludes`.
 *
 * ## Why time is where the billing date is
 *
 * A subscriber opens Billing to find out what is about to leave their account
 * and when. Somebody on a trial opens it to find out how long they have got.
 * Same corner of the card, same weight, different question — and there is no
 * charge to date, so putting the trial's end date there in the guise of a
 * billing date would be quoting a payment nobody has agreed to.
 *
 * The "Active" pill wears the same fill a running subscription's does, because
 * it is saying the same thing: everything is unlocked. A trial that has run out
 * has no card at all — the workspace is back to the price list — so there is no
 * ended state for this one to draw.
 */
const TrialPlanCard: React.FC<TrialPlanCardProps> = ({
  productName,
  countdown,
}) => (
  <section
    aria-labelledby="trial-plan-heading"
    className="rounded-md border border-[#E7E8EC] bg-white"
  >
    <div className="flex flex-wrap items-start justify-between gap-4 px-5 pt-4 pb-3 3xl:px-6 3xl:py-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2
            id="trial-plan-heading"
            className="text-[20px] 3xl:text-[24px] font-semibold text-ink"
          >
            {/* The trial is named on the plan rather than in a pill beside it:
                it is what this is, not a state it is passing through, and the
                pill beside it is already spoken for by whether it is running. */}
            {productName} (Trial)
          </h2>
          <span
            className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[12px] 3xl:text-[13px] font-medium ${TONE_CLASSES.good}`}
          >
            Active
          </span>
        </div>

        <p className="mt-1.5 text-[13px] 3xl:text-[14px] text-ink-muted">
          One plan. Complete access
        </p>
      </div>

      <div className="text-right">
        <p className="text-[12px] 3xl:text-[13px] text-ink-muted">
          Remaining time
        </p>
        {/* The words the rail's meter uses — "3 days left", "Ends today" — so
            the two places a trial is counted never disagree by a day. */}
        <p className="mt-0.5 text-[16px] 3xl:text-[18px] font-semibold text-ink">
          {countdown.label}
        </p>
      </div>
    </div>

    <PlanIncludes />
  </section>
);

export default TrialPlanCard;
